import { ISelectItem } from "@components/inputs/select/Select.types";
import {
    getSimpleBoolSelectItems,
    ifAll,
    ifAny,
    IFieldDef,
    IFieldInfoProperties,
    IGetValueArgs,
    not,
    TGetValueFn
} from "@components/smart/FieldInfo";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import {
    PrPayslipEmploymentBenefitEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEmploymentExtraSalaryEntity,
    PrPayslipEmploymentRetirementSavingEntity,
    PrPayslipEmploymentSalaryComponentEntity,
    PrPayslipEntity,
    PrRetirementSavingsProductEntity,
    PrSalaryComponentTemporalEntity,
} from "@odata/GeneratedEntityTypes";
import {
    PrBenefitTypeCode,
    PrExtraSalaryComputationTypeCode,
    PrExtraSalaryTypeCode,
    PrRegularSalaryTypeCode,
    PrRetirementSavingsComputationTypeCode,
    PrSalaryComponentTypeCode
} from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import { userNameWithAvatarFormatter } from "@pages/admin/users/Users.utils";
import {
    BenefitEmployeeTotalPath,
    BenefitEmployerTotalPath,
    BenefitTotalPath,
    getBenefitFieldDefinition,
    getMealVoucherShareUnitFormatter,
    isMealVoucher,
    isOtherBenefit
} from "@pages/payroll/benefits/Benefit.utils";
import { ExtraSalaryAmountPath, getExtraSalaryFieldDefinition } from "@pages/payroll/extraSalary/ExtraSalary.utils";
import { getPayrollCurrencyFieldDef, getPayrollCurrencyUnit } from "@pages/payroll/payroll.utils";
import {
    EMPLOYEES_TEMPLATE_ID,
    IBulkAddPayslipTargetEmployment,
    TEMPLATE_EMPLOYMENT_ID
} from "@pages/payroll/payslip/bulkAddItems/BulkAddPayslipItems.utils";
import { BulkAddItemType, productPath } from "@pages/payroll/payslip/Payslip.utils";
import {
    getRetirementSavingsBasicFields,
    getRetirementSavingsComputationTypeDef,
    getRetirementSavingsSecondaryFields,
} from "@pages/payroll/retirementSavingsCompany/RetirementSavings.utils";
import { getSalaryComponentFieldDefinition } from "@pages/payroll/salaryComponent/SalaryComponent.utils";
import i18next from "i18next";

import { BasicInputSizes, FieldType, TextAlign, ValidatorType } from "../../../../enums";
import { TValue } from "../../../../global.types";
import { IDefinition, IGetDefinitionOptionalTranslation } from "../../../../pages/PageUtils";
import { IFormDef } from "../../../../views/formView/Form";

const localContext = BindingContext.localContext;

const RowEmployeePath = localContext("Employee");
const RowEmployeeNumberPath = localContext("EmployeeNumber");
const RowEmploymentsPath = localContext("Employments");
const RowEmploymentPath = localContext("Employment");

export const commonBulkAddFieldPaths = {
    "Employee": RowEmployeePath,
    "EmployeeNumber": RowEmployeeNumberPath,
    "Employments": RowEmploymentsPath,
    "Employment": RowEmploymentPath,
} as const;

 type CommonBulkAddFieldPath = keyof typeof commonBulkAddFieldPaths
const commonBulkAddFieldKeys: CommonBulkAddFieldPath[] = ["EmployeeNumber", "Employee", "Employment"];


const getCommonBulkAddFields = (): Partial<Record<CommonBulkAddFieldPath, IFieldInfoProperties>> => ({
    EmployeeNumber: {
        type: FieldType.Input,
        label: i18next.t("Payslip:BulkAddPayslipItems.EmployeeNumber"),
        width: BasicInputSizes.M,
        isReadOnly: true,
    },
    Employee: {
        type: FieldType.Input,
        label: i18next.t("Payslip:BulkAddPayslipItems.Employee"),
        width: BasicInputSizes.L,
        isReadOnly: true,
        formatter: employeeColumnFormatter,
    },
    Employment: {
        type: FieldType.ComboBox,
        label: i18next.t("Payslip:BulkAddPayslipItems.Employment"),
        width: BasicInputSizes.M,
        isVisible: isEmployeeRow,
        isRequired: isEmployeeRow,
        isReadOnly: isRowEmploymentReadOnly,
        fieldSettings: {
            items: [],
            itemsForRender: (_, args) => getRowEmploymentItems(args),
        },
    },
});

const registerCommonBulkAddFields = (): IFieldDef[] => {
    return commonBulkAddFieldKeys.map((key) => {
        return {
             id: commonBulkAddFieldPaths[key],
        };
    });
};

const defineCommonBulkAddFields = (entityPath?: (path: string) => string) => {
    return Object.entries(getCommonBulkAddFields()).reduce((acc, [key, fieldDef]) => {
        const localContextPath = commonBulkAddFieldPaths[`${key as keyof typeof commonBulkAddFieldPaths}`];
        const path = entityPath ? entityPath(localContextPath) : localContextPath;
        acc[path] = fieldDef;
        return acc;

    }, {} as Record<string, IFieldInfoProperties>);
};



export function getCollectionPath(itemType: BulkAddItemType) {
    switch (itemType) {
        case BulkAddItemType.SalaryComponent: return PrPayslipEmploymentEntity.SalaryComponents;
        case BulkAddItemType.Benefit: return PrPayslipEmploymentEntity.Benefits;
        case BulkAddItemType.ExtraSalary: return PrPayslipEmploymentEntity.ExtraSalaries;
        case BulkAddItemType.RetirementSaving: return PrPayslipEmploymentEntity.RetirementSavings;
    }
}

const commonFieldPathSet = new Set<string>(Object.values(commonBulkAddFieldPaths));

function withoutCommonFields(columns: IFieldDef[]): IFieldDef[] {
    return columns.filter(column => !commonFieldPathSet.has(column.id));
}

export function newItemColumns(itemType: BulkAddItemType): IFieldDef[] {
    switch (itemType) {
        case BulkAddItemType.SalaryComponent: return [...salaryComponentHeaderColumns, ...withoutCommonFields(salaryComponentRowColumns)];
        case BulkAddItemType.Benefit: return [...benefitHeaderColumns, ...withoutCommonFields(benefitRowColumns)];
        case BulkAddItemType.ExtraSalary: return [...extraSalaryHeaderColumns, ...withoutCommonFields(extraSalaryRowColumns)];
        case BulkAddItemType.RetirementSaving: return [...retirementSavingHeaderColumns, ...withoutCommonFields(retirementSavingRowColumns)];
    }
}

export function getHeaderColumns(itemType: BulkAddItemType): IFieldDef[] {
    switch (itemType) {
        case BulkAddItemType.SalaryComponent: return salaryComponentHeaderColumns;
        case BulkAddItemType.Benefit: return benefitHeaderColumns;
        case BulkAddItemType.ExtraSalary: return extraSalaryHeaderColumns;
        case BulkAddItemType.RetirementSaving: return retirementSavingHeaderColumns;
    }
}

export function getImplicitFields(itemType: BulkAddItemType): Record<string, unknown> {
    switch (itemType) {
        case BulkAddItemType.SalaryComponent:
        case BulkAddItemType.Benefit: return { IsOneTime: true };
        case BulkAddItemType.ExtraSalary: return { IsOneTime: true, TypeCode: PrExtraSalaryTypeCode.Custom };
        case BulkAddItemType.RetirementSaving: return { IsOneTime: true };
    }
}

export function getBulkAddDefinition(itemType: BulkAddItemType): IGetDefinitionOptionalTranslation {
    const definition: IGetDefinitionOptionalTranslation = (): IDefinition => ({
        form: getBulkAddFormDef(itemType)
    });
    definition.translationFiles = getBulkAddDefinition.translationFiles;
    return definition;
}

function getBulkAddFormDef(itemType: BulkAddItemType): IFormDef {
    switch (itemType) {
        case BulkAddItemType.SalaryComponent: return getBulkAddSalaryComponentFormDef();
        case BulkAddItemType.Benefit: return getBulkAddBenefitFormDef();
        case BulkAddItemType.ExtraSalary: return getBulkAddExtraSalaryFormDef();
        case BulkAddItemType.RetirementSaving: return getBulkAddRetirementSavingFormDef();
    }
}

function isSalaryComponentType(...types: PrSalaryComponentTypeCode[]): TGetValueFn<boolean> {
    return (args: IGetValueArgs): boolean => {
        const typeBc = args.bindingContext.getParent().navigate(PrPayslipEmploymentSalaryComponentEntity.Type);
        const code = args.storage.getValue(typeBc, { useDirectValue: false });
        return !code || types.includes(code as PrSalaryComponentTypeCode);
    };
}

function isExplicitSalaryComponentType(...types: PrSalaryComponentTypeCode[]): TGetValueFn<boolean> {
    return (args: IGetValueArgs): boolean => {
        const typeBc = args.bindingContext.getParent().navigate(PrPayslipEmploymentSalaryComponentEntity.Type);
        const code = args.storage.getValue(typeBc, { useDirectValue: false });
        return !!code && types.includes(code as PrSalaryComponentTypeCode);
    };
}

function isExplicitBenefitType(type: PrBenefitTypeCode): TGetValueFn<boolean> {
    return (args: IGetValueArgs): boolean => {
        const typeBc = args.bindingContext.getParent().navigate(PrPayslipEmploymentBenefitEntity.Type);
        const code = args.storage.getValue(typeBc, { useDirectValue: false });
        return code === type;
    };
}

function getExtraSalaryComputationCode(args: IGetValueArgs): PrExtraSalaryComputationTypeCode | undefined {
    const computationBc = args.bindingContext.getParent().navigate(PrPayslipEmploymentExtraSalaryEntity.ComputationType);
    return args.storage.getValue(computationBc, { useDirectValue: false }) as PrExtraSalaryComputationTypeCode | undefined;
}

// cannot compute without access to payslip
function isExtraSalaryAmountVisible(args: IGetValueArgs): boolean {
    return getExtraSalaryComputationCode(args) !== PrExtraSalaryComputationTypeCode.AverageHourlySalaryPercentage;
}

function isHourlyExtraSalaryForCompensation(args: IGetValueArgs): boolean {
    const computationType = getExtraSalaryComputationCode(args);
    if (![
        PrExtraSalaryComputationTypeCode.AbsoluteAmountHour,
        PrExtraSalaryComputationTypeCode.AverageHourlySalaryPercentage,
    ].includes(computationType)) {
        return false;
    }

    const selectedEmploymentId = args.storage.getValue(args.bindingContext.getParent().navigate(RowEmploymentPath));
    return getRowEmployments(args).some(employment => employment.payslipEmploymentId === selectedEmploymentId && employment.isCompensation);
}

function isHourlySalaryComponentForCompensation(args: IGetValueArgs): boolean {
    const parentBc = args.bindingContext.getParent();
    const typeBc = parentBc.navigate(PrPayslipEmploymentSalaryComponentEntity.Type);
    const regularSalaryTypeBc = parentBc.navigate(PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType);
    const type = args.storage.getValue(typeBc, { useDirectValue: false });
    const regularSalaryType = args.storage.getValue(regularSalaryTypeBc, { useDirectValue: false });

    if (type !== PrSalaryComponentTypeCode.Regular || regularSalaryType !== PrRegularSalaryTypeCode.Hourly) {
        return false;
    }

    const selectedEmploymentId = args.storage.getValue(parentBc.navigate(RowEmploymentPath));
    return getRowEmployments(args).some(employment => employment.payslipEmploymentId === selectedEmploymentId && employment.isCompensation);
}

export const RetirementSavingsTypePath = BindingContext.localContext("RetirementSavingsType");



function employeeColumnFormatter(val: TValue, args: IFormatOptions): ReturnType<typeof userNameWithAvatarFormatter> {
    return userNameWithAvatarFormatter(val, { ...args, entity: val as IEntity });
}

function getRowEmployments(args: IGetValueArgs): IBulkAddPayslipTargetEmployment[] {
    const employmentsBc = args.bindingContext.getParent().navigate(RowEmploymentsPath);
    return (args.storage.getValue(employmentsBc) as IBulkAddPayslipTargetEmployment[]) ?? [];
}

function getRowEmploymentItems(args: IGetValueArgs): ISelectItem[] {
    return getRowEmployments(args).map(employment => ({
        id: employment.payslipEmploymentId,
        label: employment.name,
    }));
}

function isRowEmploymentReadOnly(args: IGetValueArgs): boolean {
    return getRowEmployments(args).length <= 1;
}

function isEmployeeRow(args: IGetValueArgs): boolean {
    return args.bindingContext.getParent().getParent().getKey() === EMPLOYEES_TEMPLATE_ID;
}

export const salaryComponentHeaderColumns: IFieldDef[] = [
    { id: PrPayslipEmploymentSalaryComponentEntity.Name },
    { id: PrPayslipEmploymentSalaryComponentEntity.Type },
    { id: PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType },
    { id: PrPayslipEmploymentSalaryComponentEntity.FixedBonusTaxImpact },
    { id: PrPayslipEmploymentSalaryComponentEntity.FixedBonusPayOutImpact },
    { id: PrPayslipEmploymentSalaryComponentEntity.FixedBonusDeductionImpact },
    { id: PrPayslipEmploymentSalaryComponentEntity.TimeBasedBonusType },
    { id: PrPayslipEmploymentSalaryComponentEntity.AverageInclusion },
];

export const salaryComponentRowColumns: IFieldDef[] = [
    ...registerCommonBulkAddFields(),
    { id: PrSalaryComponentTemporalEntity.IsBasicSalary },
    { id: PrPayslipEmploymentSalaryComponentEntity.PercentageOverride },
    { id: PrPayslipEmploymentSalaryComponentEntity.AmountOverride },
];

const getBulkAddGroupsDef = (args: {
    collection: string,
    order: string,
    headerColumns: IFieldDef[],
    lineItemColumns: IFieldDef[],
}) => {
    return [
        {
            id: "common",
            collection: PrPayslipEntity.Employments,
            collectionKey: TEMPLATE_EMPLOYMENT_ID,
            lineItems: {
                collection: args.collection,
                columns: args.headerColumns,
                canReorder: false,
                canAdd: false,
                isItemRemovable: false,
                isItemCloneable: false,
                isAddButtonTransparent: false,
                order: args.order,
            }
        },
        {
            id: "employments",
            title: i18next.t("Payslip:BulkAddPayslipItems.Employees"),
            collection: PrPayslipEntity.Employments,
            collectionKey: EMPLOYEES_TEMPLATE_ID,
            lineItems: {
                collection: args.collection,
                columns: args.lineItemColumns,
                canReorder: false,
                canAdd: false,
                isItemRemovable: false,
                isItemCloneable: false,
                isAddButtonTransparent: false,
                order: args.order,
            }
        }
    ];

};



function getBulkAddSalaryComponentFormDef(): IFormDef {
    const salaryComponentPath = (field: string) => createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, field);

    return {
        id: "BulkAddPayslipItemsDialog",
        translationFiles: getBulkAddDefinition.translationFiles,
        fieldDefinition: {
            [salaryComponentPath(PrPayslipEmploymentSalaryComponentEntity.Name)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.Name),
                isRequired: true,
            },
            [salaryComponentPath(PrPayslipEmploymentSalaryComponentEntity.Type)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.Type),
                isReadOnly: false,
                isRequired: true,
            },
            [salaryComponentPath(PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType),
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.Regular),
                isRequired: isExplicitSalaryComponentType(PrSalaryComponentTypeCode.Regular),
            },
            [salaryComponentPath(PrPayslipEmploymentSalaryComponentEntity.FixedBonusTaxImpact)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.FixedBonusTaxImpact),
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.Fixed),
                isRequired: false,
            },
            [salaryComponentPath(PrPayslipEmploymentSalaryComponentEntity.FixedBonusPayOutImpact)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.FixedBonusPayOutImpact),
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.Fixed),
                isRequired: false,
            },
            [salaryComponentPath(PrPayslipEmploymentSalaryComponentEntity.FixedBonusDeductionImpact)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.FixedBonusDeductionImpact),
                isRequired: false,
            },
            [salaryComponentPath(PrPayslipEmploymentSalaryComponentEntity.TimeBasedBonusType)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.TimeBasedBonusType),
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.TimeBasedBonus),
                isRequired: false,
            },
            [salaryComponentPath(PrPayslipEmploymentSalaryComponentEntity.AverageInclusion)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.AverageInclusion),
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.TimeBasedBonus),
                isRequired: false,
            },
            [salaryComponentPath(PrSalaryComponentTemporalEntity.IsBasicSalary)]: {
                ...getSalaryComponentFieldDefinition(PrSalaryComponentTemporalEntity.IsBasicSalary),
                isVisible: ifAll(getSalaryComponentFieldDefinition(PrSalaryComponentTemporalEntity.IsBasicSalary).isVisible, isEmployeeRow),
            },
            [salaryComponentPath(PrPayslipEmploymentSalaryComponentEntity.PercentageOverride)]: {
                ...getSalaryComponentFieldDefinition(PrSalaryComponentTemporalEntity.Percentage),
                isVisible: ifAll(getSalaryComponentFieldDefinition(PrSalaryComponentTemporalEntity.Percentage).isVisible, isEmployeeRow),
                isDisabled: isHourlySalaryComponentForCompensation,
                isRequired: ifAll(isEmployeeRow, not(isHourlySalaryComponentForCompensation)),
            },
            [salaryComponentPath(PrPayslipEmploymentSalaryComponentEntity.AmountOverride)]: {
                label: i18next.t("Payslip:Form.AmountOverride"),
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.Amount),
                isVisible: isEmployeeRow,
                isDisabled: isHourlySalaryComponentForCompensation,
                isRequired: ifAll(isEmployeeRow, not(isHourlySalaryComponentForCompensation)),
                validator: {
                    type: ValidatorType.Custom,
                    settings: {
                        customValidator: (value, args) => {
                            return isHourlySalaryComponentForCompensation(args) || value === null || value === undefined || (typeof value === "number" && value > 0);
                        },
                        message: "Validation.Above",
                    },
                },
            },
            ...defineCommonBulkAddFields(salaryComponentPath),
        },
        groups: getBulkAddGroupsDef({
            collection: PrPayslipEmploymentEntity.SalaryComponents,
            headerColumns: salaryComponentHeaderColumns,
            lineItemColumns: salaryComponentRowColumns,
            order: PrPayslipEmploymentSalaryComponentEntity.Id
        })
    };
}

export const benefitHeaderColumns: IFieldDef[] = [
    { id: PrPayslipEmploymentBenefitEntity.Name },
    { id: PrPayslipEmploymentBenefitEntity.Type },
];

export const benefitRowColumns: IFieldDef[] = [
    ...registerCommonBulkAddFields(),
    { id: PrPayslipEmploymentBenefitEntity.OtherCategory },
    { id: PrPayslipEmploymentBenefitEntity.TaxAndContributionsApplicability },
    { id: PrPayslipEmploymentBenefitEntity.TaxDeducibility },
    { id: PrPayslipEmploymentBenefitEntity.AmountOverride },
    { id: PrPayslipEmploymentBenefitEntity.AmountEmployeeOverride },
    { id: PrPayslipEmploymentBenefitEntity.AmountEmployerOverride },
    { id: PrPayslipEmploymentBenefitEntity.LengthOverride },
    { id: BenefitEmployerTotalPath },
    { id: BenefitEmployeeTotalPath },
    { id: BenefitTotalPath },
];

function getBulkAddBenefitFormDef(): IFormDef {
    const amountEmployeeDef = getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.AmountEmployee);
    const amountEmployerDef = getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.AmountEmployer);
    const otherCategoryDef = getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.OtherCategory);
    const taxAndContributionsApplicabilityDef = getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.TaxAndContributionsApplicability);
    const taxDeducibilityDef = getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.TaxDeducibility);
    const benefitPath = (field: string) => createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, field);

    return {
        id: "BulkAddPayslipItemsDialog",
        translationFiles: getBulkAddDefinition.translationFiles,
        fieldDefinition: {
            [benefitPath(PrPayslipEmploymentBenefitEntity.Name)]: {
                ...getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.Name),
                isRequired: true,
            },
            [benefitPath(PrPayslipEmploymentBenefitEntity.Type)]: {
                ...getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.Type),
                isReadOnly: false,
                isRequired: true,
            },
            [benefitPath(PrPayslipEmploymentBenefitEntity.OtherCategory)]: {
                ...otherCategoryDef,
                isVisible: ifAll(otherCategoryDef.isVisible, isEmployeeRow),
                isRequired: isExplicitBenefitType(PrBenefitTypeCode.OtherBenefit),
            },
            [benefitPath(PrPayslipEmploymentBenefitEntity.TaxAndContributionsApplicability)]: {
                ...taxAndContributionsApplicabilityDef,
                isVisible: ifAll(taxAndContributionsApplicabilityDef.isVisible, isEmployeeRow),
                isRequired: isExplicitBenefitType(PrBenefitTypeCode.OtherBenefit),
            },
            [benefitPath(PrPayslipEmploymentBenefitEntity.TaxDeducibility)]: {
                ...taxDeducibilityDef,
                isVisible: ifAll(taxDeducibilityDef.isVisible, isEmployeeRow),
                isRequired: isExplicitBenefitType(PrBenefitTypeCode.OtherBenefit),
            },
            [benefitPath(PrPayslipEmploymentBenefitEntity.AmountOverride)]: {
                ...getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.Amount),
                label: i18next.t("Payslip:Form.AmountOverride"),
                isVisible: isEmployeeRow,
            },
            [benefitPath(PrPayslipEmploymentBenefitEntity.AmountEmployeeOverride)]: {
                ...amountEmployeeDef,
                label: i18next.t("Benefit:Form.EmployeeAmount"),
                isVisible: ifAll(isMealVoucher, isEmployeeRow),
                isRequired: isExplicitBenefitType(PrBenefitTypeCode.MealVouchers),
                fieldSettings: {
                    ...amountEmployeeDef.fieldSettings,
                    unit: getMealVoucherShareUnitFormatter(PrPayslipEmploymentBenefitEntity.AmountOverride)
                },
            },
            [benefitPath(PrPayslipEmploymentBenefitEntity.AmountEmployerOverride)]: {
                ...amountEmployerDef,
                label: i18next.t("Benefit:Form.EmployerAmount"),
                isVisible: ifAll(isMealVoucher, isEmployeeRow),
                isRequired: isExplicitBenefitType(PrBenefitTypeCode.MealVouchers),
                fieldSettings: {
                    ...amountEmployerDef.fieldSettings,
                    unit: getMealVoucherShareUnitFormatter(PrPayslipEmploymentBenefitEntity.AmountOverride)
                },
            },
            [benefitPath(PrPayslipEmploymentBenefitEntity.LengthOverride)]: {
                ...getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.LengthOverride),
                width: BasicInputSizes.XS,
                textAlign: TextAlign.Right,
                isRequired: ifAll(not(isOtherBenefit), isEmployeeRow),
                isVisible: ifAll(not(isOtherBenefit), isEmployeeRow),
                fieldSettings: {
                    showSteppers: false,
                    min: 0
                },
            },
            [benefitPath(BenefitEmployerTotalPath)]: {
                ...getBenefitFieldDefinition(BenefitEmployerTotalPath),
                isVisible: ifAll(isMealVoucher, isEmployeeRow),
                width: BasicInputSizes.S,
                textAlign: TextAlign.Right,
            },
            [benefitPath(BenefitEmployeeTotalPath)]: {
                ...getBenefitFieldDefinition(BenefitEmployeeTotalPath),
                isVisible: ifAll(isMealVoucher, isEmployeeRow),
                width: BasicInputSizes.S,
                textAlign: TextAlign.Right,
            },
            [benefitPath(BenefitTotalPath)]: {
                ...getBenefitFieldDefinition(BenefitTotalPath),
                isVisible: ifAll(not(ifAny(isMealVoucher, isOtherBenefit)), isEmployeeRow),
                width: BasicInputSizes.S,
                textAlign: TextAlign.Right,
            },
            ...defineCommonBulkAddFields(benefitPath),
        },
        groups: getBulkAddGroupsDef({
            collection: PrPayslipEmploymentEntity.Benefits,
            headerColumns: benefitHeaderColumns,
            lineItemColumns: benefitRowColumns,
            order: PrPayslipEmploymentBenefitEntity.Id,
        })
    };
}


export const extraSalaryHeaderColumns: IFieldDef[] = [
    { id: PrPayslipEmploymentExtraSalaryEntity.Name },
    { id: PrPayslipEmploymentExtraSalaryEntity.ComputationType },
];

export const extraSalaryRowColumns: IFieldDef[] = [
    ...registerCommonBulkAddFields(),
    { id: PrPayslipEmploymentExtraSalaryEntity.LengthOverride },
    { id: PrPayslipEmploymentExtraSalaryEntity.ValueOverride },
    { id: ExtraSalaryAmountPath },
];

function getBulkAddExtraSalaryFormDef(): IFormDef {
    const extraSalaryPath = (field: string) => createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, field);
    const currencyFieldDef = getPayrollCurrencyFieldDef();

    return {
        id: "BulkAddPayslipItemsDialog",
        translationFiles: getBulkAddDefinition.translationFiles,
        fieldDefinition: {
            [extraSalaryPath(PrPayslipEmploymentExtraSalaryEntity.Name)]: {
                ...getExtraSalaryFieldDefinition(PrPayslipEmploymentExtraSalaryEntity.Name),
                isReadOnly: false,
            },
            [extraSalaryPath(PrPayslipEmploymentExtraSalaryEntity.ComputationType)]: {
                ...getExtraSalaryFieldDefinition(PrPayslipEmploymentExtraSalaryEntity.ComputationType),
                width: BasicInputSizes.L,
                isReadOnly: false,
                isRequired: true,
                affectedFields: undefined,
            },
            [extraSalaryPath(PrPayslipEmploymentExtraSalaryEntity.LengthOverride)]: {
                ...getExtraSalaryFieldDefinition(PrPayslipEmploymentExtraSalaryEntity.LengthOverride),
                width: BasicInputSizes.S,
                label: i18next.t("Payslip:Form.Length"),
                isDisabled: isHourlyExtraSalaryForCompensation,
                isRequired: ifAll(isEmployeeRow, not(isHourlyExtraSalaryForCompensation)),
                isVisible: isEmployeeRow,
            },
            [extraSalaryPath(PrPayslipEmploymentExtraSalaryEntity.ValueOverride)]: {
                type: FieldType.NumberInput,
                width: BasicInputSizes.S,
                label: i18next.t("Payslip:Form.ValueOverride"),
                isDisabled: isHourlyExtraSalaryForCompensation,
                isRequired: ifAll(isEmployeeRow, not(isHourlyExtraSalaryForCompensation)),
                isVisible: isEmployeeRow,
                ...currencyFieldDef,
                fieldSettings: {
                    ...currencyFieldDef.fieldSettings,
                    unit: (args: IGetValueArgs): string => {
                        const code = getExtraSalaryComputationCode(args);
                        switch (code) {
                            case PrExtraSalaryComputationTypeCode.AverageHourlySalaryPercentage:
                                return "%";
                            case PrExtraSalaryComputationTypeCode.AbsoluteAmountHour:
                                return `${getPayrollCurrencyUnit()(args)}/h`;
                            default:
                                return getPayrollCurrencyUnit()(args);
                        }
                    },
                    showSteppers: false,
                    min: 0
                },
                validator: {
                    type: ValidatorType.Number,
                    settings: { min: 0 }
                },
            },
            [extraSalaryPath(ExtraSalaryAmountPath)]: {
                ...getExtraSalaryFieldDefinition(ExtraSalaryAmountPath),
                width: BasicInputSizes.S,
                isVisible: ifAll(isExtraSalaryAmountVisible, isEmployeeRow),
            },
            ...defineCommonBulkAddFields(extraSalaryPath),
        },
        groups: getBulkAddGroupsDef({
            collection: PrPayslipEmploymentEntity.ExtraSalaries,
            headerColumns: extraSalaryHeaderColumns,
            lineItemColumns: extraSalaryRowColumns,
            order: PrPayslipEmploymentExtraSalaryEntity.Id,
        })
    };
}

export const retirementSavingTypeColumnId = createPath(PrPayslipEmploymentRetirementSavingEntity.RetirementSavingsProduct, PrRetirementSavingsProductEntity.Type);

function isRetirementSavingMandatoryContribution(args: IGetValueArgs): boolean {
    const computationBc = args.bindingContext.getParent().navigate(PrPayslipEmploymentRetirementSavingEntity.ComputationType);
    const code = args.storage.getValue(computationBc, { useDirectValue: false });
    return code === PrRetirementSavingsComputationTypeCode.MandatoryContribution;
}

export const retirementSavingHeaderColumns: IFieldDef[] = [
    { id: retirementSavingTypeColumnId },
    { id: productPath },
    { id: PrPayslipEmploymentRetirementSavingEntity.ComputationType },
];

export const retirementSavingRowColumns: IFieldDef[] = [
    ...registerCommonBulkAddFields(),
    { id: PrPayslipEmploymentRetirementSavingEntity.IsIncluded },
    { id: PrPayslipEmploymentRetirementSavingEntity.AmountOverride },
    { id: PrPayslipEmploymentRetirementSavingEntity.PercentageOverride },
    { id: PrPayslipEmploymentRetirementSavingEntity.SymbolVariableOverride },
    { id: PrPayslipEmploymentRetirementSavingEntity.SymbolSpecificOverride },
    { id: PrPayslipEmploymentRetirementSavingEntity.SymbolConstantOverride },
    { id: PrPayslipEmploymentRetirementSavingEntity.RemittanceInformationOverride },
];

function getBulkAddRetirementSavingFormDef(): IFormDef {
    const retirementSavingPath = (field: string) => createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.RetirementSavings, field);
    const basicFields = getRetirementSavingsBasicFields(PrPayslipEntity.Employments);
    const secondaryFields = getRetirementSavingsSecondaryFields();

    return {
        id: "BulkAddPayslipItemsDialog",
        translationFiles: getBulkAddDefinition.translationFiles,
        fieldDefinition: {
            ...basicFields,
            [retirementSavingPath(retirementSavingTypeColumnId)]: {
                ...basicFields[retirementSavingPath(retirementSavingTypeColumnId)],
                isRequired: true,
            },
            [retirementSavingPath(PrPayslipEmploymentRetirementSavingEntity.ComputationType)]: {
                ...getRetirementSavingsComputationTypeDef(),
                isRequired: true,
                affectedFields: undefined,
            },
            [retirementSavingPath(PrPayslipEmploymentRetirementSavingEntity.IsIncluded)]: {
                type: FieldType.ComboBox,
                fieldSettings: {
                    items: getSimpleBoolSelectItems(),
                },
                isVisible: isEmployeeRow,
                isRequired: ifAll(isRetirementSavingMandatoryContribution, isEmployeeRow),
                isDisabled: not(isRetirementSavingMandatoryContribution),
            },
            ...Object.fromEntries(
                Object.entries(secondaryFields).map(([key, value]) => [
                    retirementSavingPath(`${key}Override`),
                    {
                        ...value,
                        isVisible: value.isVisible ? ifAll(value.isVisible, isEmployeeRow) : isEmployeeRow,
                    },
                ])
            ),
            ...defineCommonBulkAddFields(retirementSavingPath),
        },
        groups: getBulkAddGroupsDef({
            collection: PrPayslipEmploymentEntity.RetirementSavings,
            headerColumns: retirementSavingHeaderColumns,
            lineItemColumns: retirementSavingRowColumns,
            order: PrPayslipEmploymentRetirementSavingEntity.Id,
        })
    };
}

getBulkAddDefinition.translationFiles = ["Payslip", "Common", "SalaryComponent", "Benefit", "ExtraSalary", "RetirementSavingsCompany"];
